import { CommonModule } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DataTableComponent, TableColumn } from '../../shared/components/tables/data-table/data-table.component';
import { AdminMechanicsService } from '../../core/services/admin-mechanics.service';
import { AdminMechanic } from '../../core/models/admin.models';

/** Flattened shape the DataTable renders */
export interface MechanicRow {
  id: number;
  userId: number;
  name: string;
  profileImage: string;
  phone: string;
  email: string;
  area: string;
  skillset: string[];
  rating: number;
  verificationStatus: 'Pending' | 'Approved' | 'Rejected';
  accountStatus: 'Active' | 'Suspended' | 'Inactive';
}

@Component({
  selector: 'app-mechanics-list',
  standalone: true,
  imports: [CommonModule, DataTableComponent],
  templateUrl: './mechanics-list.component.html',
  styleUrls: ['./mechanics-list.component.css']
})
export class MechanicsListComponent implements OnInit {

  mechanics: MechanicRow[] = [];
  isLoading = false;
  errorMessage = '';
  columns: TableColumn[] = [];

  @ViewChild('actionTemplate')
  set actionTpl(tpl: TemplateRef<any>) {
    if (tpl) {
      setTimeout(() => {
        this.columns = [
          { header: 'Mechanic', field: 'name', type: 'profile' },
          { header: 'Area',     field: 'area',   type: 'string' },
          { header: 'Skillset', field: 'skillset', type: 'array' },
          { header: 'Rating',   field: 'rating',   type: 'rating' },
          { header: 'Verification', field: 'verificationStatus', type: 'badge' },
          { header: 'Account Status', field: 'accountStatus',  type: 'badge' },
          { header: 'Actions',  field: 'actions',  type: 'actions', cellTemplate: tpl }
        ];
      });
    }
  }

  constructor(
    private router: Router,
    private mechanicsService: AdminMechanicsService
  ) {}

  ngOnInit(): void {
    this.loadMechanics();
  }

  loadMechanics(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.mechanicsService.getMechanics().subscribe({
      next: (res) => {
        const list: AdminMechanic[] = Array.isArray(res)
          ? res
          : (res as any).data ?? [];
        this.mechanics = list.map(m => this.toRow(m));
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? 'Failed to load mechanics.';
        this.isLoading = false;
      }
    });
  }

  /** Map API AdminMechanic → MechanicRow for the table */
  private toRow(m: AdminMechanic): MechanicRow {
    const profile = m.mechanic_profile;
    return {
      id:     m.id,
      userId: m.id,
      name:   m.name,
      profileImage: m.avatar_url ?? `https://i.pravatar.cc/40?u=${m.id}`,
      phone:  m.phone,
      email:  m.email,
      area:   profile?.area ?? '—',
      skillset: profile?.skills ?? [],
      rating:   profile?.average_rating ?? 0,
      verificationStatus: profile?.is_verified ? 'Approved' : 'Pending',
      accountStatus: m.is_active ? 'Active' : 'Suspended'
    };
  }

  viewMechanic(row: MechanicRow): void {
    this.router.navigate(['/mechanics-profile', row.id]);
  }

  verifyMechanic(row: MechanicRow): void {
    const newValue = row.verificationStatus !== 'Approved';
    this.mechanicsService.verifyMechanic(row.id, newValue).subscribe({
      next: () => this.loadMechanics(),
      error: (err) => alert(err?.error?.message ?? 'Verify action failed.')
    });
  }

  toggleBlock(row: MechanicRow): void {
    const action$ = row.accountStatus === 'Active'
      ? this.mechanicsService.blockMechanic(row.userId)
      : this.mechanicsService.unblockMechanic(row.userId);

    action$.subscribe({
      next: () => this.loadMechanics(),
      error: (err) => alert(err?.error?.message ?? 'Block/Unblock action failed.')
    });
  }
}
